import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/site";
import { scrollTo } from "./Reveal";

const EASE = [0.22, 1, 0.36, 1];

const LINES = [
  <>Build wealth through</>,
  <>
    real estate <span className="italic text-gold font-light">without</span>
  </>,
  <>becoming a full-time investor.</>,
];

const Line = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay: 0.35 + i * 0.16, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Fade = ({ children, delay, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay, ease: EASE }} className={className}>
    {children}
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 60, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative min-h-screen pt-32 pb-16 lg:pt-40 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -top-40 -left-40 h-[640px] w-[640px] rounded-full bg-gold/10 blur-[160px]" />
      <motion.span
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2, delay: 1 }}
        className="pointer-events-none select-none absolute right-[-2%] top-[8%] font-serif text-[28vw] leading-none text-transparent [-webkit-text-stroke:1px_rgba(212,175,55,0.09)]"
        aria-hidden
      >
        2022
      </motion.span>

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <Fade delay={0.15}>
            <span className="eyebrow flex items-center gap-4">
              <span className="relative inline-flex h-2 w-2 rounded-full bg-gold pulse-ring" />
              {CONTACT.legalName} · DC · MD · VA
            </span>
          </Fade>

          <h1 className="mt-8 font-serif font-light text-4xl sm:text-5xl lg:text-[5.4rem] leading-[1.02] tracking-[-0.015em] text-ivory" data-testid="hero-title">
            {LINES.map((l, i) => (
              <Line key={i} i={i}>
                {l}
              </Line>
            ))}
          </h1>

          <Fade delay={1} className="mt-10 max-w-xl">
            <p className="text-base sm:text-lg text-ivory/65 leading-relaxed font-light">
              We help busy professionals participate in real estate through professionally sourced acquisitions,
              private lending relationships, renovation strategy and long-term wealth planning — powered by
              traditional valuation and modern AI.
            </p>
          </Fade>

          <Fade delay={1.15} className="mt-10 flex flex-wrap items-center gap-5">
            <a
              href={CONTACT.calendly}
              target="_blank"
              rel="noreferrer"
              className="btn-gold group inline-flex items-center gap-3 bg-gold text-obsidian text-xs tracking-[0.2em] uppercase px-7 py-4"
              data-testid="hero-calendly-btn"
            >
              Schedule an Investor Call
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button
              onClick={() => scrollTo("#film")}
              className="group inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-ivory/80 hover:text-gold transition-colors"
              data-testid="hero-watch-btn"
            >
              <span className="relative grid place-items-center h-10 w-10 rounded-full border border-ivory/30 group-hover:border-gold transition-colors">
                <Play size={12} className="ml-0.5 fill-current" />
              </span>
              Watch the film
            </button>
          </Fade>
        </motion.div>

        <div className="lg:col-span-5 [perspective:1400px]">
          <motion.div
            style={{ rotateX: rx, rotateY: ry, y: imgY }}
            initial={{ opacity: 0, scale: 0.94, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0 0 0% 0)" }}
            transition={{ duration: 1.4, delay: 0.6, ease: EASE }}
            className="relative"
            data-testid="hero-image-frame"
          >
            <div className="absolute -inset-3 border border-gold/25 translate-x-4 translate-y-4" />
            <div className="clip-estate relative aspect-[4/5] overflow-hidden bg-ink">
              <img src={IMAGES.hero} alt="Modern estate property" className="h-full w-full object-cover scale-105" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
            </div>
            <div className="absolute left-6 bottom-6 flex flex-col gap-1">
              <span className="eyebrow">Est. {CONTACT.founded}</span>
              <span className="font-serif text-xl text-ivory">{CONTACT.location}</span>
            </div>
            <div className="absolute -right-3 top-8 h-16 w-px bg-gold" />
            <div className="absolute -right-3 top-8 w-16 h-px bg-gold" />
          </motion.div>
        </div>
      </div>

      <Fade delay={1.6} className="relative mx-auto max-w-[1440px] px-6 lg:px-12 mt-20">
        <div className="gold-rule mb-6" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="hero-stats">
          {[
            ["Family-owned", "Since 2022"],
            ["Region", "DC · MD · VA"],
            ["Underwriting", "AI + Traditional"],
            ["Model", "BRRRR Concierge"],
          ].map(([k, v]) => (
            <div key={k}>
              <div className="eyebrow">{k}</div>
              <div className="mt-2 font-serif text-xl text-ivory/90">{v}</div>
            </div>
          ))}
        </div>
      </Fade>
    </section>
  );
};
